import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, MapPin, Phone, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', city: '', address: '', phone: '', email: '', capacity: 100 });

  const fetch = async () => {
    try { const { data } = await api.get('/api/branches'); setBranches(data.branches || []); } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try { await api.post('/api/branches', form); toast.success('Branch created'); setShowCreate(false); setForm({ name: '', code: '', city: '', address: '', phone: '', email: '', capacity: 100 }); fetch(); } catch (err) { toast.error('Failed'); }
  };

  return (
    <div data-testid="branches-page" className="stagger-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Branches</h1>
          <p className="text-sm text-muted-foreground mt-1"><span className="font-mono">{branches.length}</span> locations</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-branch-btn" className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}><Plus size={14} weight="bold" /> Add Branch</Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl max-w-md">
            <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Add Branch</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branch Name *</Label><Input data-testid="branch-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Code *</Label><Input data-testid="branch-code" value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="h-9 rounded-lg text-sm" required placeholder="MUM-XYZ" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">City *</Label><Input data-testid="branch-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
              </div>
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Address</Label><Input data-testid="branch-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</Label><Input data-testid="branch-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Capacity</Label><Input data-testid="branch-capacity" type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value) || 0})} className="h-9 rounded-lg text-sm" /></div>
              </div>
              <Button type="submit" data-testid="submit-branch-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create Branch</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map(b => (
            <Card key={b.id} data-testid={`branch-card-${b.id}`} className="border border-border rounded-xl card-lift">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-display text-sm font-bold text-foreground">{b.name}</h3>
                    <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider mt-1 inline-block">{b.code}</span>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${b.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                </div>
                <div className="space-y-2 text-[12px] text-muted-foreground">
                  <div className="flex items-center gap-2"><MapPin size={13} /><span>{b.address || b.city}</span></div>
                  <div className="flex items-center gap-2"><Phone size={13} /><span className="font-mono">{b.phone || '-'}</span></div>
                  <div className="flex items-center gap-2"><Users size={13} /><span>Capacity: <span className="font-mono text-foreground">{b.capacity}</span></span></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
